'use client';

import { useParams, usePathname, useSearchParams } from 'next/navigation';
import { useRenderStore } from '@/features/design/render/model/render-form.store';
import type { AssistantScreen } from '@/types/assistant';

export interface AssistantScreenContext {
  screen: AssistantScreen;
  buildingId: string | null;
  designId: string | null;
  /** The render the design screen shows (the settings form's source). */
  renderId: string | null;
}

/**
 * Where the user is, in the API's vocabulary (`AssistantScreenEnum`). Read
 * from the route the app already treats as the source of truth for the
 * building, the design and its tab.
 */
export function useAssistantScreen(): AssistantScreenContext {
  const pathname = usePathname() ?? '';
  const params = useParams<{ projectId?: string; conceptId?: string }>();
  const searchParams = useSearchParams();
  const formContext = useRenderStore((state) => state.currentContext);
  const showRenderDetails = useRenderStore((state) => state.showRenderDetails);

  const buildingId = params?.projectId ?? null;
  const designId = params?.conceptId ?? null;
  const renderId =
    designId && formContext?.conceptId === designId
      ? (formContext.renderId ?? null)
      : null;

  return {
    screen: screenOf(pathname, designId, searchParams?.get('tab') ?? null, showRenderDetails),
    buildingId,
    designId,
    renderId,
  };
}

function screenOf(
  pathname: string,
  designId: string | null,
  tab: string | null,
  showRenderDetails: boolean
): AssistantScreen {
  if (pathname === '/') return 'buildings';
  if (designId) {
    if (tab === 'estimate') return 'estimate';
    if (tab === 'album') return 'album';
    return showRenderDetails ? 'render' : 'design';
  }
  if (/^\/project\/[^/]+\/goals$/.test(pathname)) return 'brief';
  if (/^\/project\/[^/]+/.test(pathname)) return 'building';
  return 'other';
}
